import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const Card = styled(motion.div)`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

const MemberImage = styled.div`
  height: 250px;
  background-size: cover;
  background-position: center;
  position: relative;
  
  .member-branch {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: #6c5ce7;
    color: white;
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
  }
`;

const MemberContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MemberName = styled.h3`
  margin-bottom: 0.5rem;
  color: #333;
  font-size: 1.5rem;
`;

const MemberDetails = styled.div`
  margin-bottom: 1rem;
  
  .detail {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
    color: #666;
    
    svg {
      margin-right: 0.5rem;
      color: #6c5ce7;
    }
  }
`;

const MemberModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`;

const MemberModalContent = styled(motion.div)`
  background: white;
  border-radius: 10px;
  max-width: 600px;
  width: 100%;
  overflow: hidden;
  position: relative;
  
  .close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: white;
    z-index: 10;
  }
  
  .modal-image {
    height: 250px;
    background-size: cover;
    background-position: center;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.6));
    }
    
    .modal-name {
      position: absolute;
      bottom: 2rem;
      left: 2rem;
      color: white;
      z-index: 1;
      
      h2 {
        font-size: 2rem;
        margin-bottom: 0.5rem;
      }
      
      .branch {
        display: inline-block;
        background: #6c5ce7;
        padding: 0.3rem 0.8rem;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 500;
      }
    }
  }
  
  .modal-content {
    padding: 2rem;
    
    .bio {
      margin-bottom: 2rem;
      
      h3 {
        color: #6c5ce7;
        margin-bottom: 1rem;
      }
      
      p {
        color: #666;
        line-height: 1.6;
      }
    }
    
    .contact-info {
      h3 {
        color: #6c5ce7;
        margin-bottom: 1rem;
      }
      
      .contact-detail {
        display: flex;
        align-items: center;
        margin-bottom: 1rem;
        
        svg {
          margin-right: 0.5rem;
          color: #6c5ce7;
        }
        
        a {
          color: #666;
          text-decoration: none;
          
          &:hover {
            color: #6c5ce7;
            text-decoration: underline;
          }
        }
      }
    }
  }
`;

const MemberCard = ({ member }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <Card
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                onClick={() => setIsModalOpen(true)}
            >
                <MemberImage style={{ backgroundImage: `url(${member.image})` }}>
                    <div className="member-branch">{member.branch} Branch</div>
                </MemberImage>
                <MemberContent>
                    <MemberName>{member.name}</MemberName>
                    <MemberDetails>
                        <div className="detail">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
                            </svg>
                            Age: {member.age}
                        </div>
                        <div className="detail">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
                            </svg>
                            {member.location}
                        </div>
                        <div className="detail">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 6H4V4H20V6ZM20 11H4V9H20V11ZM20 16H4V14H20V16ZM20 21H4V19H20V21Z" fill="currentColor"/>
                            </svg>
                            {member.occupation}
                        </div>
                    </MemberDetails>
                </MemberContent>
            </Card>

            <AnimatePresence>
                {isModalOpen && (
                    <MemberModal
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsModalOpen(false)}
                    >
                        <MemberModalContent
                            onClick={e => e.stopPropagation()}
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 50, opacity: 0 }}
                        >
                            <button className="close-button" onClick={() => setIsModalOpen(false)}>✕</button>
                            <div className="modal-image" style={{ backgroundImage: `url(${member.image})` }}>
                                <div className="modal-name">
                                    <h2>{member.name}</h2>
                                    <span className="branch">{member.branch} Branch</span>
                                </div>
                            </div>
                            <div className="modal-content">
                                <div className="bio">
                                    <h3>About</h3>
                                    <p>{member.bio}</p>
                                </div>
                                <div className="contact-info">
                                    <h3>Contact Information</h3>
                                    <div className="contact-detail">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor"/>
                                        </svg>
                                        <a href={`mailto:${member.email}`}>{member.email}</a>
                                    </div>
                                    <div className="contact-detail">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" fill="currentColor"/>
                                        </svg>
                                        <a href={`tel:${member.phone.replace(/[^0-9]/g, '')}`}>{member.phone}</a>
                                    </div>
                                </div>
                            </div>
                        </MemberModalContent>
                    </MemberModal>
                )}
            </AnimatePresence>
        </>
    );
};

export default MemberCard;